import jwtDecode from 'jwt-decode';
import { isAuthDisabled } from '@/store/auth/auth-utils';
import { playClientTs } from '@/client/play-client';
import { ActionContext, ActionTree } from 'vuex';
import { AuthState, JwtClaims } from '@/store/auth/types';
import { State } from '@/store/play-root-state';
import { stateLog } from '@/store/config/types';
import { auth, getAccessToken } from '@/plugins/auth';

const actions = <ActionTree<AuthState, State>>{
    FETCH_USER_DETAILS(context) {
        return fetchUserDetails(context);
    },

    LOGIN(context, jwt:string) {
        return login(context, jwt);
    },

    // Auth0 counterpart of LOGIN. Reuses the same SESSION_START shape so
    // existing getters (isLoggedIn, etc.) and the FETCH_USER_DETAILS
    // subscriber in store/index.ts keep working without changes.
    LOGIN_WITH_AUTH0(context, payload:{ accessToken:string, auth0User?:Record<string, unknown> }) {
        return loginWithAuth0(context, payload);
    },

    VERIFY_LOGIN(context) {
        return verifyLogin(context);
    },

    LOGOUT({ commit }) {
        commit('SESSION_END');
    },
};

const log = stateLog('auth', 'actions');

type AuthContext = ActionContext<AuthState, unknown>;

async function fetchUserDetails({ commit, state }:AuthContext) {
    const { session: { jwt }, accounts, userDetailsFetched } = state;

    if (jwt && !accounts) {
        try {
            // For non-embedded (standalone) usage, delegate to the Auth0 SDK
            // so silent refreshes (Safari ITP fallback, rotated refresh
            // tokens, etc.) automatically get picked up on every API call
            // without re-dispatching LOGIN. Embedded mode keeps the static
            // JWT from the store.
            if (auth?.isAuthenticated?.value) {
                playClientTs.request.config.TOKEN = async () => (await getAccessToken()) ?? jwt;
            } else {
                playClientTs.request.config.TOKEN = jwt;
            }

            playClientTs.request.config.WITH_CREDENTIALS = true;

            if (!userDetailsFetched) {
                commit('SET_USER_DETAILS_FETCHED', true);
                const accountsInfo = await playClientTs.user.getUserInfo();

                log.info('From API', accountsInfo);
                commit('SET_USER_DETAILS', accountsInfo);
                commit('SET_USER_ROLES', accountsInfo.roles);

                log.info('User details already being fetched');

                return accountsInfo;
            }

            return null;
        } catch (e) {
            log.error('Error fetching', e);
        }
    }

    log.warn('No user details could be fetched');

    return null;
}

const login = ({ commit }:AuthContext, jwt?:string) => {
    if (jwt) {
        try {
            const {
                email, firstName, id, lastName, displayName, exp,
            } = jwtDecode<JwtClaims>(jwt) ?? {};

            if (isJwtExpired(exp)) {
                commit('SESSION_END');
            } else {
                const user = {
                    id,
                    username: email,
                    givenName: firstName,
                    familyName: lastName,
                    fullName: displayName,
                    exp,
                };

                commit('SESSION_START', { jwt, user });
            }
        } catch (e) {
            commit('SESSION_FAIL');
        }
    } else {
        commit('SESSION_END');
    }
};

const verifyLogin = ({ state, commit }:AuthContext) => {
    if (!isAuthDisabled() && isJwtExpired(state.user?.exp)) {
        commit('SESSION_END');
    }
};

/**
 * Hydrate the auth store from an Auth0 SPA session.
 *
 * The Auth0 access token may be opaque (no decodable payload) or signed but
 * lacking legacy claims (`id`, `firstName`, etc.). We try to decode it for
 * any custom Keap claims an Auth0 Action may have injected, then fall back
 * to the SDK's `auth.user.value` (sourced from the id_token) for display
 * fields. The canonical user record is still hydrated immediately afterwards
 * by FETCH_USER_DETAILS via the play-api `/v2/user` endpoint.
 */
const loginWithAuth0 = (
    { commit, state }:AuthContext,
    { accessToken, auth0User }:{ accessToken:string, auth0User?:Record<string, unknown> },
) => {
    if (!accessToken) {
        commit('SESSION_END');

        return;
    }

    let tokenClaims:JwtClaims = {};

    try {
        tokenClaims = jwtDecode<JwtClaims>(accessToken) ?? {};
    } catch {
        // Opaque access token — fall back to id_token-sourced auth0User claims.
    }

    const fullName = (auth0User?.name as string)
        ?? tokenClaims.displayName
        ?? [tokenClaims.firstName, tokenClaims.lastName].filter(Boolean).join(' ');

    const user = {
        id: tokenClaims.id
            ?? (auth0User?.['https://keap.com/id'] as string)
            ?? (auth0User?.sub as string)
            ?? '',
        username: tokenClaims.email
            ?? (auth0User?.email as string)
            ?? (auth0User?.preferred_username as string)
            ?? '',
        givenName: tokenClaims.firstName
            ?? (auth0User?.given_name as string)
            ?? '',
        familyName: tokenClaims.lastName
            ?? (auth0User?.family_name as string)
            ?? '',
        fullName,
        exp: tokenClaims.exp ?? 0,
    };

    if (isJwtExpired(user.exp)) {
        commit('SESSION_END');

        return;
    }

    // Same-user silent token rotation must not wipe accounts. Reserve
    // SESSION_START for a real login or user switch.
    const isSameUserSession = Boolean(
        state.session?.jwt && state.user?.id && state.user.id === user.id,
    );

    commit(isSameUserSession ? 'SESSION_REFRESH' : 'SESSION_START', { jwt: accessToken, user });
};

const isJwtExpired = (exp:number) => {
    if (exp) {
        return exp * 1000 < Date.now();
    }

    return true;
};

export default actions;
